import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Boxes, GitBranch, PanelLeft, ScanSearch, Waypoints } from 'lucide-react';
import { Button, Drawer, Tabs } from '../ui';
import EtchPreview, { buildEtchDraft, buildMissionPayload } from '../etch/EtchPreview';
import { apiClient } from '../../api/client';
import { useSession } from '../../context/SessionContext';
import { useNavIntent } from '../../context/NavigationContext';
import { GRAPH_MODES, INTENT_PATHS, SOURCES, STORAGE_KEYS, SURFACES } from './constants';
import { usePersistentState, useMediaQuery, useOnlineStatus } from './hooks';
import {
  directorySelection,
  documentSelection,
  etchSubject,
  explainPrompt,
  fileSelection,
  isActionable,
  symbolSelection
} from './selection';
import DetailPanel from './DetailPanel';
import ExplorerRail from './ExplorerRail';
import GraphSurface from './GraphSurface';
import ImpactPanel from './ImpactPanel';
import PanelState from './PanelState';
import SelectionBar from './SelectionBar';
import SourcePreview from './SourcePreview';

const DESKTOP = '(min-width: 1200px)';

/** Id of the synthetic source descriptor built from the current selection. */
const ETCH_SOURCE_ID = 'selection';

/** Maps the rail's `{item, kind}` callback onto the shared selection model. */
function toSelection({ item, kind }) {
  switch (kind) {
    case 'directory':
      return directorySelection(item);
    case 'document':
      return documentSelection(item);
    case 'symbol':
      return symbolSelection(item);
    default:
      return fileSelection(item);
  }
}

/**
 * Der Wissensexplorer (spec §9.5, §10.5).
 *
 * One area that unifies documents, files, code, call graph, architecture graph
 * and source preview behind a single search, where every meaningful selection
 * can be *erklärt* (opens chat with the selection as context) or *eingeprägt*
 * (hands the selection to `EtchPreview`, which owns the mission write path).
 *
 * Layout follows the three deliberate shapes of spec §8: explorer rail + main
 * panel + optional detail panel on desktop, drawers below 1200px. The page body
 * never scrolls horizontally — wide tables and graphs scroll inside their own
 * box.
 */
export function KnowledgeExplorer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { consumeIntent, navigate: navWithData } = useNavIntent();
  const { workspacePath, status: sessionStatus, error: sessionError } = useSession();

  const isDesktop = useMediaQuery(DESKTOP);
  const online = useOnlineStatus();

  const [layout, setLayout] = usePersistentState(STORAGE_KEYS.layout, {
    detailOpen: true,
    source: SOURCES.files
  });
  const [graphMode, setGraphMode] = usePersistentState(STORAGE_KEYS.graphMode, GRAPH_MODES.graph);

  const [surface, setSurface] = useState(SURFACES.preview);
  const [selection, setSelection] = useState(null);
  const [archMode, setArchMode] = useState('structure');
  const [impactTarget, setImpactTarget] = useState('');
  const [impactRunToken, setImpactRunToken] = useState(0);
  const [railDrawerOpen, setRailDrawerOpen] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [etchOpen, setEtchOpen] = useState(false);
  const [etchDraft, setEtchDraft] = useState(() => buildEtchDraft());
  const [etchSaving, setEtchSaving] = useState(false);
  const [etchError, setEtchError] = useState(null);
  const [missionStatus, setMissionStatus] = useState('');

  const mainHeadingId = useId();
  const detailHeadingId = useId();
  const drawerDetailHeadingId = useId();

  const source = layout.source || SOURCES.files;
  const setSource = useCallback((next) => setLayout((prev) => ({ ...prev, source: next })), [setLayout]);
  const detailOpen = layout.detailOpen !== false;
  const toggleDetail = useCallback(
    () => setLayout((prev) => ({ ...prev, detailOpen: prev.detailOpen === false })),
    [setLayout]
  );

  // Deep links from other views. `/ide` stays accepted because the onboarding
  // panel still emits the pre-Etch path while the legacy route redirects here.
  useEffect(() => {
    const intent = consumeIntent();
    if (!intent || !INTENT_PATHS.includes(intent.path) || !intent.data) return;
    const { file, tab, vizType } = intent.data;
    if (file) {
      setSelection(fileSelection({ path: file, name: file.split('/').pop() }));
      setSurface(SURFACES.preview);
    }
    if (vizType === 'callgraph') setSurface(SURFACES.callgraph);
    else if (vizType) setSurface(SURFACES.architecture);
    else if (tab === 'graph') setSurface(SURFACES.architecture);
    else if (tab === 'actions') setSurface(SURFACES.impact);
    else if (tab === 'code') setSurface(SURFACES.preview);
    // Intent is read-and-clear; re-running would consume a later intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = useCallback((next) => {
    setSelection(next);
    setRailDrawerOpen(false);
    if (next?.kind === 'file' || next?.kind === 'directory' || next?.kind === 'document') {
      setSurface(SURFACES.preview);
    }
  }, []);

  const handleExplain = useCallback(() => {
    if (!isActionable(selection)) return;
    navWithData('/chat', { question: explainPrompt(selection, t) });
    navigate('/chat');
  }, [selection, navWithData, navigate, t]);

  const etchSelection = useMemo(
    () => (isActionable(selection) ? etchSubject(selection, { t, workspacePath }) : null),
    [selection, t, workspacePath]
  );

  // The selection itself is the only source a knowledge-explorer mission can
  // cite, expressed in the descriptor shape `EtchPreview`/`buildMissionPayload`
  // expect.
  const etchSources = useMemo(() => {
    if (!etchSelection?.sourceRef) return [];
    return [
      {
        id: ETCH_SOURCE_ID,
        kind: etchSelection.kind,
        title: etchSelection.title || etchSelection.sourceLabel,
        ref: etchSelection.sourceRef,
        locator: etchSelection.sourceLabel || null,
        cited: true,
        state: 'ok'
      }
    ];
  }, [etchSelection]);

  const handleEtch = useCallback(() => {
    if (!etchSelection) return;
    setMissionStatus('');
    setEtchError(null);
    setEtchDraft(
      buildEtchDraft({
        statement: etchSelection.keyStatement,
        goal: etchSelection.learningGoal || t('etchPreview.defaultGoal'),
        sourceId: etchSelection.sourceRef ? ETCH_SOURCE_ID : null,
        form: 'standard',
        topic: etchSelection.title
      })
    );
    setEtchOpen(true);
  }, [etchSelection, t]);

  // The write path: `EtchPreview` is pure UI, so the mission POST belongs here
  // (same contract the chat side uses through `EtchAction`).
  const handleEtchConfirm = useCallback(
    async (confirmed) => {
      setEtchSaving(true);
      setEtchError(null);
      try {
        await apiClient.request('/missions', {
          method: 'POST',
          body: JSON.stringify(buildMissionPayload(confirmed, { t, sources: etchSources, workspacePath }))
        });
        setEtchOpen(false);
        setMissionStatus(t('etchPreview.savedToast'));
      } catch (err) {
        setEtchError(err?.message || t('states.error'));
      } finally {
        setEtchSaving(false);
      }
    },
    [etchSources, t, workspacePath]
  );

  const handleImpactAnalysis = useCallback((target) => {
    if (!target) return;
    setImpactTarget(target);
    setSurface(SURFACES.impact);
    setDetailDrawerOpen(false);
    setImpactRunToken((token) => token + 1);
  }, []);

  const rail = (
    <ExplorerRail source={source} onSourceChange={setSource} selection={selection} onSelect={(payload) => handleSelect(toSelection(payload))} />
  );

  const detail = (headingId) => (
    <DetailPanel
      selection={selection}
      headingId={headingId}
      onExplain={handleExplain}
      onEtch={handleEtch}
      onImpactAnalysis={handleImpactAnalysis}
    />
  );

  const surfaceItems = [
    {
      id: SURFACES.preview,
      label: (
        <>
          <ScanSearch size={15} aria-hidden="true" />
          {t('knowledge.preview')}
        </>
      ),
      panel:
        surface === SURFACES.preview ? (
          <SourcePreview
            selection={selection}
            onSelect={handleSelect}
            onShowArchitecture={() => setSurface(SURFACES.architecture)}
          />
        ) : null
    },
    {
      id: SURFACES.architecture,
      label: (
        <>
          <Boxes size={15} aria-hidden="true" />
          {t('knowledge.architecture')}
        </>
      ),
      panel:
        surface === SURFACES.architecture ? (
          <GraphSurface
            vizType="architecture"
            selection={selection}
            mode={graphMode}
            onModeChange={setGraphMode}
            archMode={archMode}
            onArchModeChange={setArchMode}
            onNodeSelect={handleSelect}
            onImpactAnalysis={handleImpactAnalysis}
          />
        ) : null
    },
    {
      id: SURFACES.callgraph,
      label: (
        <>
          <GitBranch size={15} aria-hidden="true" />
          {t('knowledge.callGraph')}
        </>
      ),
      panel:
        surface === SURFACES.callgraph ? (
          <GraphSurface
            vizType="callgraph"
            selection={selection}
            mode={graphMode}
            onModeChange={setGraphMode}
            archMode={archMode}
            onArchModeChange={setArchMode}
            onNodeSelect={handleSelect}
            onImpactAnalysis={handleImpactAnalysis}
          />
        ) : null
    },
    {
      id: SURFACES.impact,
      label: (
        <>
          <Waypoints size={15} aria-hidden="true" />
          {t('knowledge.impact')}
        </>
      ),
      panel:
        surface === SURFACES.impact ? (
          <ImpactPanel target={impactTarget} onTargetChange={setImpactTarget} runToken={impactRunToken} />
        ) : null
    }
  ];

  const activeSurfaceLabel = {
    [SURFACES.preview]: t('knowledge.preview'),
    [SURFACES.architecture]: t('knowledge.architecture'),
    [SURFACES.callgraph]: t('knowledge.callGraph'),
    [SURFACES.impact]: t('knowledge.impact')
  }[surface];

  return (
    <div className="etch-knowledge" data-detail={isDesktop && detailOpen ? 'open' : undefined}>
      <header className="etch-page-header etch-knowledge__header">
        <div className="etch-page-header__title">
          <h1>{t('knowledge.title')}</h1>
        </div>
        <div className="etch-page-header__actions">
          {!isDesktop && (
            <Button
              variant="secondary"
              size="compact"
              onClick={() => setRailDrawerOpen(true)}
              aria-expanded={railDrawerOpen}
            >
              <PanelLeft size={15} aria-hidden="true" />
              {t('ide.explorer')}
            </Button>
          )}
          {isDesktop && (
            <Button variant="ghost" size="compact" onClick={toggleDetail} aria-expanded={detailOpen}>
              {t('knowledge.details')}
            </Button>
          )}
        </div>
      </header>

      {!online && (
        <PanelState status="offline" description={t('knowledge.offlineHint')} compact />
      )}
      {online && sessionStatus === 'error' && (
        <PanelState status="error" error={sessionError} description={t('ide.connectFailed')} compact />
      )}

      <div className="etch-knowledge__body">
        {isDesktop && (
          <nav className="etch-knowledge__rail-column" aria-label={t('ide.explorer')}>
            {rail}
          </nav>
        )}

        <section className="etch-knowledge__main" aria-labelledby={mainHeadingId}>
          <h2 id={mainHeadingId} className="etch-visually-hidden">
            {activeSurfaceLabel}
          </h2>

          <SelectionBar
            selection={selection}
            onExplain={handleExplain}
            onEtch={handleEtch}
            showDetailsToggle={!isDesktop}
            detailsOpen={detailDrawerOpen}
            onToggleDetails={() => setDetailDrawerOpen((open) => !open)}
          />

          <Tabs
            className="etch-knowledge__surfaces"
            ariaLabel={t('knowledge.surfaceSwitcher')}
            items={surfaceItems}
            value={surface}
            onChange={setSurface}
          />
        </section>

        {isDesktop && detailOpen && (
          <aside className="etch-knowledge__detail" aria-labelledby={detailHeadingId}>
            {detail(detailHeadingId)}
          </aside>
        )}
      </div>

      <p className="etch-visually-hidden" role="status" aria-live="polite">
        {missionStatus}
      </p>

      {!isDesktop && (
        <>
          <Drawer
            open={railDrawerOpen}
            onClose={() => setRailDrawerOpen(false)}
            side="start"
            title={t('ide.explorer')}
            closeLabel={t('a11y.closeDialog')}
          >
            {rail}
          </Drawer>
          <Drawer
            open={detailDrawerOpen}
            onClose={() => setDetailDrawerOpen(false)}
            side="end"
            title={t('knowledge.details')}
            closeLabel={t('a11y.closeDialog')}
          >
            {detail(drawerDetailHeadingId)}
          </Drawer>
        </>
      )}

      <EtchPreview
        open={etchOpen}
        onClose={() => setEtchOpen(false)}
        draft={etchDraft}
        onDraftChange={setEtchDraft}
        onConfirm={handleEtchConfirm}
        sources={etchSources}
        saving={etchSaving}
        error={etchError}
      />
    </div>
  );
}

export default KnowledgeExplorer;
